import React from 'react';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { ArrowLeft, CheckCircle2, User, PartyPopper } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

export interface OnboardingData {
  avatar: File | null;
  birthday: Date | null;
  professionalDescription: string;
  professionalHabit: string;
  friendGroupKnownFor: string;
  hobbies: string;
  lifeGoal: string;
  funFact: string;
  careerLearning: string;
}

interface CompletionStepProps {
  data: OnboardingData;
  previewUrl: string | null;
  onComplete: () => void;
  onBack: () => void;
}

export function CompletionStep({ data, previewUrl, onComplete, onBack }: CompletionStepProps) {
  const completedFields = [
    data.avatar && 'Profile photo',
    data.birthday && 'Birthday',
    data.professionalDescription && 'Professional description',
    data.professionalHabit && 'Professional habit',
    data.friendGroupKnownFor && 'Friend group role',
    data.hobbies && 'Hobbies',
    data.lifeGoal && 'Life goals',
    data.funFact && 'Fun fact',
    data.careerLearning && 'Career learning goals',
  ].filter(Boolean);

  return (
    <div className="space-y-8 text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: 'spring' }}
        className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100"
      >
        <PartyPopper className="w-12 h-12 text-green-600" />
      </motion.div>

      <div className="space-y-3">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">You're all set!</h2>
        <p className="text-lg text-muted-foreground max-w-md mx-auto">
          Thanks for sharing. Your profile is looking great.
        </p>
      </div>

      <div className="flex flex-col items-center gap-6 py-6">
        <Avatar className="w-24 h-24 border-4 border-prosigliere-blue/20">
          {previewUrl ? (
            <AvatarImage src={previewUrl} alt="Profile" />
          ) : (
            <AvatarFallback className="bg-secondary">
              <User className="w-10 h-10 text-muted-foreground" />
            </AvatarFallback>
          )}
        </Avatar>

        {data.birthday && (
          <p className="text-muted-foreground">
            Birthday: <span className="text-foreground font-medium">{format(data.birthday, 'MMMM d')}</span>
          </p>
        )}

        <div className="flex flex-wrap justify-center gap-2 max-w-md">
          {completedFields.map((field, index) => (
            <motion.span
              key={field}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm"
            >
              <CheckCircle2 className="w-3 h-3" />
              {field}
            </motion.span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Go back
        </Button>
        <Button onClick={onComplete} size="lg" className="rounded-full px-8">
          Complete Setup
        </Button>
      </div>
    </div>
  );
}
